import React, { useMemo, useState } from "react";
import axios from "axios";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { Button } from "components/buttonV2";
import { AiFillDelete } from "react-icons/ai";
import { BsDownload } from "react-icons/bs";

const BASE = `${process.env.REACT_APP_SERVER_URL}/api/v1/eserv-business/special-reports`;

const getCommissionOverrideStorageKey = (employeeId: number | string) =>
  `special-report:commissionPercent:${String(employeeId)}`;

const getDefaultWeekBounds = (): { weekStart: string; weekEnd: string } => {
  const now = new Date();
  const dayOfWeek = now.getDay();
  const daysSinceMonday = (dayOfWeek + 6) % 7;
  const currentWeekMonday = new Date(now);
  currentWeekMonday.setDate(currentWeekMonday.getDate() - daysSinceMonday);
  const weekEnd = new Date(currentWeekMonday);
  weekEnd.setDate(weekEnd.getDate() - 1);
  const weekStart = new Date(weekEnd);
  weekStart.setDate(weekStart.getDate() - 6);
  const toYYYYMMDD = (d: Date) => d.toISOString().slice(0, 10);
  return { weekStart: toYYYYMMDD(weekStart), weekEnd: toYYYYMMDD(weekEnd) };
};

type ReportRow = {
  id: number;
  s3_key: string;
  created_at: string;
  week_start: string | null;
  week_end: string | null;
  record_count: number | null;
};

type Props = {
  viewedEmployeeId: number | string;
};

const SpecialReportModal = ({ viewedEmployeeId }: Props) => {
  const queryClient = useQueryClient();
  const defaultWeek = useMemo(() => getDefaultWeekBounds(), []);
  const [weekStart, setWeekStart] = useState(defaultWeek.weekStart);
  const [weekEnd, setWeekEnd] = useState(defaultWeek.weekEnd);
  // Backends in this codebase sometimes key employees as `userId`.
  // Send both to avoid mismatches while keeping behavior employee-scoped.
  const employeeParams = useMemo(
    () => ({ employeeId: viewedEmployeeId, userId: viewedEmployeeId }),
    [viewedEmployeeId]
  );
  const [commissionPercentOverride, setCommissionPercentOverride] = useState<number>(() => {
    try {
      const raw = localStorage.getItem(getCommissionOverrideStorageKey(viewedEmployeeId));
      const val = raw == null ? NaN : Number(raw);
      return Number.isFinite(val) ? val : 0;
    } catch {
      return 0;
    }
  });

  const reportsQuery = useQuery(
    ["special-reports", String(viewedEmployeeId)],
    async () => {
      const res = await axios.get(BASE, { params: employeeParams });
      return res.data as ReportRow[];
    }
  );

  const generateMutation = useMutation(
    async () => {
      try {
        localStorage.setItem(
          getCommissionOverrideStorageKey(viewedEmployeeId),
          String(commissionPercentOverride ?? 0)
        );
      } catch {
        // ignore storage failures (private mode / blocked storage)
      }

      const res = await axios.post(BASE + "/generate", {
        employeeId: viewedEmployeeId,
        userId: viewedEmployeeId,
        weekStart,
        weekEnd,
        commissionPercentOverride: commissionPercentOverride ?? 0,
      });
      return res.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["special-reports", String(viewedEmployeeId)]);
      },
    }
  );

  const downloadReport = async (id: number) => {
    const res = await axios.get(`${BASE}/${id}/download`, {
      params: employeeParams,
    });
    const url = res.data?.downloadUrl;
    if (url) window.open(url, "_blank");
  };

  const deleteMutation = useMutation(
    async (id: number) => {
      await axios.delete(`${BASE}/${id}`, { params: employeeParams });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["special-reports", String(viewedEmployeeId)]);
      },
    }
  );

  const formatTime = (createdAt: string) => {
    try {
      return new Date(createdAt).toLocaleTimeString(undefined, {
        hour: "numeric",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      });
    } catch {
      return createdAt;
    }
  };

  const formatTimeframe = (weekStart: string | null, weekEnd: string | null) => {
    if (!weekStart || !weekEnd) return "—";
    try {
      const s = new Date(weekStart).toLocaleDateString();
      const e = new Date(weekEnd).toLocaleDateString();
      return `${s} – ${e}`;
    } catch {
      return `${weekStart} – ${weekEnd}`;
    }
  };

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex flex-col gap-2">
        <h3 className="font-medium">Generate special report</h3>
        <div className="flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-1">
            <span>Commission %:</span>
            <input
              type="number"
              inputMode="decimal"
              step="0.01"
              value={commissionPercentOverride}
              onChange={(e) => setCommissionPercentOverride(Number(e.target.value))}
              className="border rounded px-2 py-1 w-[120px]"
            />
          </label>
          <span className="text-sm text-slate-600">
            (employee #{String(viewedEmployeeId)})
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-1">
            <span>Week start:</span>
            <input
              type="date"
              value={weekStart}
              onChange={(e) => setWeekStart(e.target.value)}
              className="border rounded px-2 py-1"
            />
          </label>
          <label className="flex items-center gap-1">
            <span>Week end:</span>
            <input
              type="date"
              value={weekEnd}
              onChange={(e) => setWeekEnd(e.target.value)}
              className="border rounded px-2 py-1"
            />
          </label>
          <Button
            onClick={() => generateMutation.mutate()}
            disabled={generateMutation.isLoading}
          >
            {generateMutation.isLoading ? "Generating…" : "Generate"}
          </Button>
        </div>
        {generateMutation.isSuccess && (
          <p className="text-sm text-green-600">Report generated successfully.</p>
        )}
        {generateMutation.isError && (
          <p className="text-sm text-red-600">Failed to generate report.</p>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <h3 className="font-medium">Past reports</h3>
        {reportsQuery.isLoading && <p>Loading…</p>}
        {reportsQuery.isError && <p className="text-red-600">Failed to load reports.</p>}
        {reportsQuery.data && reportsQuery.data.length === 0 && (
          <p className="text-gray-500">No reports yet.</p>
        )}
        {reportsQuery.data && reportsQuery.data.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-gray-300">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-gray-300 px-2 py-1 text-left">ID</th>
                  <th className="border border-gray-300 px-2 py-1 text-left">Count</th>
                  <th className="border border-gray-300 px-2 py-1 text-left">Time</th>
                  <th className="border border-gray-300 px-2 py-1 text-left">Timeframe</th>
                  <th className="border border-gray-300 px-2 py-1 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {reportsQuery.data.map((row) => (
                  <tr key={row.id}>
                    <td className="border border-gray-300 px-2 py-1">{row.id}</td>
                    <td className="border border-gray-300 px-2 py-1">
                      {row.record_count != null ? row.record_count : "—"}
                    </td>
                    <td className="border border-gray-300 px-2 py-1">
                      {formatTime(row.created_at)}
                    </td>
                    <td className="border border-gray-300 px-2 py-1">
                      {formatTimeframe(row.week_start, row.week_end)}
                    </td>
                    <td className="border border-gray-300 px-2 py-1">
                      <div className="flex gap-2">
                      <button
                        onClick={() => downloadReport(row.id)}
                        className="text-green-500 p-1.5 rounded-md border hover:bg-slate-200 hover:scale-105 transition-all duration-300"
                      >
                        <BsDownload  size={20} />
                      </button>
                      <button
                        className="text-red-500 p-1.5 rounded-md border hover:bg-slate-200 hover:scale-105 transition-all duration-300"
                        onClick={() => deleteMutation.mutate(row.id)}
                        disabled={deleteMutation.isLoading}
                      >
                        <AiFillDelete size={20} />
                      </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default SpecialReportModal;
